import { auth } from "./firebase";
import { crmGetScanDownloadManifestCallable, crmLogScanFileDownloadCallable } from "./crmApi";

const PROJECT_ID = "alpine-realm-473117-f9";
const REGION = "us-central1";
/** Streams zip bundles (and single files when no signed link could be made). */
export const SCAN_DOWNLOAD_URL = `https://${REGION}-${PROJECT_ID}.cloudfunctions.net/crmScanDownload`;

/**
 * Every file of the scan's storage folder, signed links (15 min), bundle sizes,
 * recent downloads. `refresh`: the panel renewing its links (not logged).
 */
export async function loadScanDownloadManifest(scanId, { refresh = false } = {}) {
  const fn = crmGetScanDownloadManifestCallable();
  const res = await fn({ scanId: String(scanId), ...(refresh ? { refresh: true } : {}) });
  return res.data;
}

/**
 * A signed link goes straight to storage, so the CRM functions never see the
 * download: record the click in the download log. Fire and forget.
 */
export function logDirectFileDownload(scanId, row) {
  const fn = crmLogScanFileDownloadCallable();
  fn({
    scanId: String(scanId),
    fileId: String(row.id),
    filename: row.filename || row.download_name || "",
    ...(row.size != null ? { size: row.size } : {}),
  }).catch(() => {});
}

/**
 * The counts crmScanDownload writes in a bundle's zip comment
 * (`… files=N unreadable=N …`), from the last bytes of the zip; null when the
 * bytes do not end with such an end record.
 */
export function bundleCountsFromZipTail(tail) {
  for (let i = tail.length - 22; i >= 0; i -= 1) {
    if (tail[i] !== 0x50 || tail[i + 1] !== 0x4b || tail[i + 2] !== 0x05 || tail[i + 3] !== 0x06) continue;
    const commentLength = tail[i + 20] | (tail[i + 21] << 8);
    if (i + 22 + commentLength !== tail.length) continue;
    const comment = String.fromCharCode(...tail.subarray(i + 22));
    const files = /\bfiles=(\d+)/.exec(comment);
    const unreadable = /\bunreadable=(\d+)/.exec(comment);
    if (!files || !unreadable) return null;
    return { files: Number(files[1]), unreadable: Number(unreadable[1]) };
  }
  return null;
}

/** Keeps the last `size` bytes of a stream (the zip's end record and comment). */
function tailKeeper(size) {
  let tail = new Uint8Array(0);
  return {
    push(chunk) {
      if (chunk.length >= size) {
        tail = chunk.slice(chunk.length - size);
        return;
      }
      const keep = Math.min(tail.length, size - chunk.length);
      const next = new Uint8Array(keep + chunk.length);
      next.set(tail.subarray(tail.length - keep), 0);
      next.set(chunk, keep);
      tail = next;
    },
    get bytes() {
      return tail;
    },
  };
}

export function formatBytes(bytes) {
  const n = Number(bytes);
  if (bytes == null || !Number.isFinite(n)) return "—";
  if (n < 1000) return `${n} B`;
  const units = ["kB", "MB", "GB", "TB"];
  let value = n / 1000;
  let unit = 0;
  while (value >= 1000 && unit < units.length - 1) {
    value /= 1000;
    unit += 1;
  }
  return `${value >= 100 ? Math.round(value) : value.toFixed(1)} ${units[unit]}`;
}

/** Chrome / Edge: save straight to a file the user picks, with progress in the page. */
export function canStreamToDisk() {
  return typeof window !== "undefined" && typeof window.showSaveFilePicker === "function";
}

function utcOffsetMinutes() {
  return -new Date().getTimezoneOffset();
}

async function idToken() {
  const user = auth.currentUser;
  if (!user) throw new Error("Sign in to download scan files.");
  return user.getIdToken();
}

async function responseError(res) {
  const text = await res.text().catch(() => "");
  try {
    const parsed = JSON.parse(text);
    if (parsed?.error) return String(parsed.error);
  } catch (_) {
    /* not JSON */
  }
  return text.trim().slice(0, 300) || `Download failed (HTTP ${res.status}).`;
}

async function postDownload(body, { signal } = {}) {
  const token = await idToken();
  return fetch(SCAN_DOWNLOAD_URL, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify({ ...body, tzOffset: utcOffsetMinutes() }),
    signal,
  });
}

export function downloadErrorMessage(err) {
  const message = String(err?.message || err || "");
  if (err?.name === "TypeError" || /failed to fetch|network/i.test(message)) {
    return "The download was interrupted (network error). Nothing was saved; try again.";
  }
  return message || "Download failed.";
}

/**
 * Chrome creates (or empties) the file as soon as it is picked in the save
 * dialog, before anything is downloaded. After a failed or cancelled save,
 * remove that empty file (FileSystemHandle.remove, Chrome 110+); returns
 * false when it could not be removed.
 */
async function discardPickedFile(handle) {
  try {
    if (typeof handle.remove !== "function") return false;
    await handle.remove();
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * Asks where to save (call it straight from the click: the picker needs the
 * user's gesture), then streams the response into that file. Progress goes to
 * `onProgress({ phase, received, expected, compressed })`. The bytes go to a
 * temporary file that replaces the chosen one only when complete; after a
 * failure or cancel the (already emptied) chosen file is removed, or the
 * error carries `leftEmptyFile` with its name when the browser cannot.
 * `unreadable`: items of a bundle that could not be read (from its zip comment).
 * @returns {Promise<{ saved: true, bytes: number, filename: string, unreadable: number | null } | { cancelled: true }>}
 */
export async function saveDownloadToDisk({ scanId, what, id, suggestedName, signal, onProgress }) {
  let handle;
  try {
    handle = await window.showSaveFilePicker({
      suggestedName,
      ...(String(suggestedName).endsWith(".zip")
        ? { types: [{ description: "Zip archive", accept: { "application/zip": [".zip"] } }] }
        : {}),
    });
  } catch (e) {
    if (e?.name === "AbortError") return { cancelled: true };
    throw e;
  }
  let writable = null;
  const tail = tailKeeper(4096);
  let received = 0;
  try {
    onProgress?.({ phase: "preparing", received: 0, expected: null });
    const res = await postDownload({ scanId, what, ...(id ? { id } : {}) }, { signal });
    if (!res.ok) throw new Error(await responseError(res));
    const expected = Number(res.headers.get("X-Scanbrix-Source-Bytes")) || null;
    const compressed = res.headers.get("X-Scanbrix-Compressed") === "1";
    writable = await handle.createWritable();
    const reader = res.body.getReader();
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      await writable.write(value);
      tail.push(value);
      received += value.length;
      onProgress?.({ phase: "downloading", received, expected, compressed });
    }
    await writable.close();
  } catch (e) {
    if (writable) await writable.abort().catch(() => {});
    if (!(await discardPickedFile(handle))) {
      try {
        e.leftEmptyFile = handle.name;
      } catch (_) {
        /* frozen error object */
      }
    }
    throw e;
  }
  const counts = what === "file" ? null : bundleCountsFromZipTail(tail.bytes);
  return { saved: true, bytes: received, filename: handle.name, unreadable: counts ? counts.unreadable : null };
}

/**
 * Other browsers: check the download first (same checks and sizes, so errors
 * show in the page), then hand it to the browser's own download manager with a
 * form post into a hidden frame. The ID token travels in the POST body, never
 * in the URL. When the server refuses the real request after the check, its
 * error page in the frame posts the message here and `onError` gets it.
 */
export async function startBrowserDownload({ scanId, what, id, onError }) {
  const res = await postDownload({ scanId, what, ...(id ? { id } : {}), plan: 1 });
  if (!res.ok) throw new Error(await responseError(res));
  const plan = await res.json();
  const token = await idToken();
  const frameName = `crm-scan-download-${Date.now().toString(36)}`;
  const frame = document.createElement("iframe");
  frame.name = frameName;
  frame.title = "Scan download";
  frame.setAttribute("aria-hidden", "true");
  frame.style.display = "none";
  document.body.appendChild(frame);
  const serverOrigin = new URL(SCAN_DOWNLOAD_URL).origin;
  let timer = null;
  const cleanup = () => {
    window.removeEventListener("message", onMessage);
    clearTimeout(timer);
    frame.remove();
  };
  function onMessage(event) {
    if (event.source !== frame.contentWindow || event.origin !== serverOrigin) return;
    if (event.data?.type !== "scanbrix-download-error") return;
    cleanup();
    onError?.(String(event.data.message || "Download failed."));
  }
  window.addEventListener("message", onMessage);
  // The frame must outlive the request (the zip is built while it streams).
  timer = setTimeout(cleanup, 65 * 60 * 1000);
  const form = document.createElement("form");
  form.method = "POST";
  form.action = SCAN_DOWNLOAD_URL;
  form.target = frameName;
  form.style.display = "none";
  const fields = {
    scanId: String(scanId),
    what,
    mode: "form",
    idToken: token,
    tzOffset: String(utcOffsetMinutes()),
    ...(id ? { id: String(id) } : {}),
  };
  for (const [name, value] of Object.entries(fields)) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }
  document.body.appendChild(form);
  form.submit();
  form.remove();
  return plan;
}
